import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import { LANGUAGES, normaliseLanguage } from "@/i18n/languages";

// Native <select>: keyboard and screen-reader support come for free, and each
// option is tagged with its own lang so it's read in the right voice.
// `compact` hides the visible "Language" label (screen readers still hear it),
// for page headers where a globe icon says enough.
export const LanguagePicker = ({ className = "", compact = false }) => {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const current = normaliseLanguage(i18n.resolvedLanguage || i18n.language);

  const onChange = async (e) => {
    const code = e.target.value;
    await i18n.changeLanguage(code); // applies now, remembered on this device
    if (!auth?.user) return;
    try {
      await http.patch("/settings/language", { language: code });
      auth.setUser((u) => (u ? { ...u, language: code } : u));
    } catch {
      toast.error(t("language.saveFailed"));
    }
  };

  return (
    <label className={`${compact ? "relative flex items-center" : "flex flex-col gap-1.5"} text-xs text-[var(--ink-soft)] ${className}`}>
      {compact
        ? <Globe size={15} aria-hidden="true" className="absolute start-3 pointer-events-none" />
        : null}
      <span className={compact ? "sr-only" : ""}>{t("language.label")}</span>
      <select value={current} onChange={onChange} data-testid="language-picker"
        className={`${compact ? "ps-9 pe-3 py-2.5 rounded-full bg-white/70" : "px-3 py-2 rounded-xl bg-[var(--paper-2)]"} border border-black/10 text-sm text-[var(--ink)]`}>
        {LANGUAGES.map((l) => (
          <option key={l.code} value={l.code} lang={l.code}>{l.nativeName}</option>
        ))}
      </select>
    </label>
  );
};
